import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrophyIcon } from '@/components/icons/TrophyIcon';
import styles from './styles.module.scss';

interface NavItem {
  path: string;
  icon: React.ReactNode;
  label: string;
  /** Extra section roots that keep this item active (beyond `path` itself). */
  activePrefixes?: string[];
}

export const BottomNav: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation('common');

  const navItems: NavItem[] = [
    {
      path: '/',
      label: t('navHome'),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <polyline points="9,22 9,12 15,12 15,22" />
        </svg>
      )
    },
    {
      path: '/tournaments',
      label: t('navTournaments'),
      icon: <TrophyIcon size={20} />
    },
    {
      path: '/matches',
      label: t('navMatchesBrowser'),
      // The standalone match page (`/match/:id`) belongs to the Matches section.
      activePrefixes: ['/match/'],
      icon: (
        // Football — matches the stroke style of the sibling items
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polygon points="12,7.5 15.8,10.3 14.4,14.8 9.6,14.8 8.2,10.3" />
          <path d="M12 2v5.5M20.5 9l-4.7 1.3M17.5 19.5l-3.1-4.7M6.5 19.5l3.1-4.7M3.5 9l4.7 1.3" />
        </svg>
      )
    },
    {
      path: '/account/edit',
      label: t('navProfile'),
      icon: <i className="icon-user" />
    }
  ];

  const isActive = ({ path, activePrefixes = [] }: NavItem) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return [path, ...activePrefixes].some(prefix => location.pathname.startsWith(prefix));
  };

  return (
    <nav className={styles.container} data-app-chrome="bottom">
      {navItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          className={`${styles.button} ${isActive(item) ? styles.active : ''}`}
          aria-label={item.label}
          data-nav-path={item.path}
          data-active={isActive(item) ? 'true' : 'false'}
        >
          {item.icon}
        </NavLink>
      ))}
    </nav>
  );
};

export default BottomNav;
